import * as Sentry from '@sentry/react';

const TIMEOUT_MS = 15000;
const AI_TIMEOUT_MS = 60000;

// In production on custom domain, API goes to pages.dev directly
const getApiBase = () => {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  // If on custom domain, route API to pages.dev
  if (host !== 'localhost' && !host.endsWith('.pages.dev')) {
    return 'https://majelani-accounting.pages.dev';
  }
  return '';
};
const API_BASE = getApiBase();

let _cachedTenantId: string | null = null;
let _cachedUserName: string | null = null;
let _cachedToken: string | null = null;

const getTenantId = () => {
  if (!_cachedTenantId) _cachedTenantId = sessionStorage.getItem('tenantId') || 'alpha-tenant-id';
  return _cachedTenantId;
};

const getUserName = () => {
  if (!_cachedUserName) { try { _cachedUserName = JSON.parse(sessionStorage.getItem('user') || '{}').name || 'Unknown'; } catch { _cachedUserName = 'Unknown'; } }
  return _cachedUserName;
};

const getAuthToken = () => {
  if (!_cachedToken) _cachedToken = sessionStorage.getItem('auth_token') || '';
  return _cachedToken;
};

export const clearApiCache = () => { _cachedTenantId = null; _cachedUserName = null; _cachedToken = null; };

export const apiFetch = async (url: string, options: RequestInit = {}) => {
  const controller = new AbortController();
  const isAI = url.includes('/ai-');
  const timeoutId = setTimeout(() => controller.abort(), isAI ? AI_TIMEOUT_MS : TIMEOUT_MS);

  try {
    const fullUrl = url.startsWith('/api') ? `${API_BASE}${url}` : url;

    const token = getAuthToken();
    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
      'x-tenant-id': getTenantId(),
      'x-user-name': getUserName(),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      ...options,
      signal: controller.signal,
      headers,
    });

    clearTimeout(timeoutId);

    // Check for HTML response FIRST (Cloudflare may return 200 with HTML for missing routes)
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('text/html')) {
      // Don't report to Sentry for known missing routes — just throw cleanly
      throw new Error(`API route not available: ${url}`);
    }

    if (!response.ok) {
      let errorMessage = `Request failed (${response.status})`;
      try {
        const text = await response.text();
        try {
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorMessage;
        } catch {
          if (text && text.length < 500) errorMessage = text;
        }
      } catch {}
      throw new Error(errorMessage);
    }

    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. The server might be busy.');
    }
    // Don't spam Sentry with transient/expected errors
    const skipSentry = error.message?.includes('API route not available') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('NetworkError') ||
      error.message?.includes('Load failed') ||
      error.name === 'TypeError';
    if (!skipSentry) {
      Sentry.captureException(error, { extra: { url, method: options.method || 'GET' } });
    }
    throw error;
  }
};
